import React from 'react';
import {
  TaskItemProps,
  TaskCheckboxContainer,
  TaskTitle,
  TaskRecurrenceDisplay,
  TaskTimeInfo,
  TaskPriorityDisplay,
  TaskNotesDisplay,
  TaskItemShared
} from '../tasks/base';
import {
  FocusProgressBar,
  FocusDurationPopover,
  FocusPlayButton
} from './components';
import { useFocusTaskItem } from './hooks';

/**
 * 专注页面的任务项组件
 * 基于TaskItem组件，但添加了播放按钮和进度条
 */
const FocusTaskItemComponent: React.FC<TaskItemProps> = ({
  task,
  onToggle,
  onClick,
  onProjectClick,
  isFirst = false,
  isLast = false,
  hideProjectName = false
}) => {
  // 使用专注任务项Hook获取所有逻辑
  const {
    // 状态
    isPlaying,
    isFocused,
    isDraggingProgressBar,
    setIsDraggingProgressBar,
    localDragProgress,
    setLocalDragProgress,
    justFinishedDragRef,
    showDurationPopover,
    popoverPosition,
    playButtonRef,
    remainingTimeMs,

    // 计算值
    backgroundColorClass,
    checkboxColorClass,
    borderStyleClass,
    isTaskProgressDraggable,
    currentProgress,
    isTimerComplete,

    // 格式化函数
    formatDate,
    formatRecurrenceFrequency,
    formatRemainingTime,

    // 事件处理器
    handleCheckboxClick,
    handleItemClick,
    handlePlayClick,
    handleDurationSelect,
    handlePauseClick,
    handleRestartClick,
    handleMouseEnterPlayButton,
    handleMouseLeavePlayButtonContainer,
    handleMouseEnterPopover,
    handleMouseLeavePopover,

    // 其他数据
    userProgressMap,
    updateUserProgress,
    trackEvent,
    t
  } = useFocusTaskItem({
    task,
    onToggle,
    onClick,
    isLast
  });

  return (
    <div
      className={`${borderStyleClass} ${backgroundColorClass} animate-ios-fade-in hover:bg-black/[0.03] dark:hover:bg-white/[0.05] transition-colors duration-200 cursor-pointer relative group ${
        isDraggingProgressBar ? 'select-none' : ''
      }`}
      onClick={handleItemClick}
    >
      {/* 进度条背景 - 使用FocusProgressBar组件 */}
      <FocusProgressBar
        task={task}
        isPlaying={isPlaying}
        isDraggingProgressBar={isDraggingProgressBar}
        setIsDraggingProgressBar={setIsDraggingProgressBar}
        localDragProgress={localDragProgress}
        setLocalDragProgress={setLocalDragProgress}
        justFinishedDragRef={justFinishedDragRef}
        currentProgress={currentProgress}
        isTaskProgressDraggable={isTaskProgressDraggable}
        isFocused={isFocused}
        updateUserProgress={updateUserProgress}
        trackEvent={trackEvent}
      />

      <div className="flex items-center relative z-10">
        {/* 左侧点击区域 - 整个左边都可以点击 */}
        <TaskCheckboxContainer
          task={task}
          onCheckboxClick={handleCheckboxClick}
          checkboxColorClass={checkboxColorClass}
          t={t}
          containerClassName="items-center justify-center"
          additionalClassName={isDraggingProgressBar ? 'select-none' : ''}
        />

        {/* 任务内容 - 更精致的 iOS 风格 */}
        <div className={`flex-1 overflow-hidden pr-4 py-3 ${
          isDraggingProgressBar ? 'select-none' : ''
        }`}>
          {/* 任务标题 - 更精致的字体样式 */}
          <TaskTitle
            task={task}
            hideProjectName={hideProjectName}
            onProjectClick={onProjectClick}
            t={t}
            titleClassName="text-ios-body font-medium truncate"
            titleColorClass="text-ios-black dark:text-white"
          />

          {/* 任务元数据 - 更精致的布局和样式 */}
          {(task.vital || task.urgent || task.startAt || task.cost || task.dueAt || task.notes || task.recurrenceRule) && (
            <div className="flex items-center mt-0.5 text-ios-footnote text-ios-gray dark:text-ios-dark-gray truncate">
              {/* 优先级标签 - 使用更精致的标签样式 */}
              <TaskPriorityDisplay task={task} t={t} />

              {/* 循环任务标识 - 与TaskItem保持一致 */}
              <TaskRecurrenceDisplay
                task={task}
                formatRecurrenceFrequency={formatRecurrenceFrequency}
              />

              {/* 时间信息 - 更精致的样式 */}
              <TaskTimeInfo
                task={task}
                formatDate={formatDate}
                t={t}
                layout="combined"
              />

              {/* 备注预览 - 更精致的样式 */}
              <TaskNotesDisplay task={task} />
            </div>
          )}
        </div>

        {/* 播放/暂停按钮 & Countdown & Popover Container */}
        <div
          className="relative ml-0 flex flex-col items-center min-w-8 mr-4"
          onMouseLeave={handleMouseLeavePlayButtonContainer}
        >
          <FocusPlayButton
            task={task}
            isPlaying={isPlaying}
            isTimerComplete={isTimerComplete}
            isDraggingProgressBar={isDraggingProgressBar}
            isTaskProgressDraggable={isTaskProgressDraggable}
            userProgressMap={userProgressMap}
            remainingTimeMs={remainingTimeMs}
            onPlayClick={handlePlayClick}
            onPauseClick={handlePauseClick}
            onRestartClick={handleRestartClick}
            onMouseEnterPlayButton={handleMouseEnterPlayButton}
            formatRemainingTime={formatRemainingTime}
            t={t}
            playButtonRef={playButtonRef}
          />

          <FocusDurationPopover
            showDurationPopover={showDurationPopover}
            popoverPosition={popoverPosition}
            onMouseEnterPopover={handleMouseEnterPopover}
            onMouseLeavePopover={handleMouseLeavePopover}
            onDurationSelect={handleDurationSelect}
            t={t}
          />
        </div>

        {/* 右箭头 - 在专注模式下完全隐藏以节省空间 */}
        <div className={`hidden`}>
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
    </div>
  );
};

// 使用 React.memo 优化性能，使用共享的比较函数
export const FocusTaskItem = React.memo(FocusTaskItemComponent, TaskItemShared.createMemoComparison());